import React, { useEffect, useState } from 'react';
import { Col, Row } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import glassEdgeTreatmentTypes from 'services/constants/glassEdgeTreatmentTypes';
import glassPropertyTypes from 'services/constants/glassPropertyTypes';
import glassTreatmentTypes from 'services/constants/glassTreatmentTypes';
import IDoorMetaData from 'components/interfaces/IDoorMetaData';
import IGlassWallDoor from 'components/interfaces/IGlassWallDoor';
import SelectInputTranslated from 'components/layout/forms/SelectInputTranslated';
import translations from 'services/translations/translations';
import { setWall, setWallMetaData } from 'store/selectedWall/actions';

import doorGlassPlacementTypes, {
  PRIMARY_GLASS,
} from 'components/configurator/types/doorGlassPlacementTypes';
import TextInputField from '../../../../../layout/forms/TextInputField';
import IWall from '../../../../../interfaces/IWall';
import getDoorsFromWallParts from '../../../../services/getDoorsFromWallParts';
import glassWallPartTypes from '../../../../types/glassWallPartTypes';

const DoorGlasses = ({
  disableInputs,
  door,
  wall,
}: {
  disableInputs?: boolean;
  door: IGlassWallDoor;
  wall: IWall;
}): JSX.Element => {
  const dispatch = useDispatch();
  const wallMetaData = useSelector((state: any) => state.selectedWall.metaData);
  const doorGlassNames = useSelector(
    (state: any) => state.selectedWall.properties.doorGlassNames
  );
  const { t } = useTranslation([translations.glassWallDoorProperties]);
  const [selectedGlass, setSelectedGlass] = useState(PRIMARY_GLASS);

  const doorMetaData = wallMetaData.doors.find(
    (data: IDoorMetaData) => data.doorTempId === door.id
  );

  const edgeTreatmentType = doorMetaData[`${selectedGlass}EdgeTreatmentType`];
  const glassPropertyType = doorMetaData[`${selectedGlass}GlassPropertyType`];
  const glassTreatmentType = doorMetaData[`${selectedGlass}GlassTreatmentType`];
  const glassName =
    doorMetaData[`${selectedGlass}Name`] != null
      ? doorMetaData[`${selectedGlass}Name`]
      : '';
  const glassNotice =
    doorMetaData[`${selectedGlass}Notice`] != null
      ? doorMetaData[`${selectedGlass}Notice`]
      : '';

  const updateDoorMetaData = (propertyName: string, value: string) => {
    dispatch(
      setWallMetaData({
        ...wallMetaData,
        doors: wallMetaData.doors.map((data: IDoorMetaData) => {
          return data.doorTempId === door.id
            ? {
                ...doorMetaData,
                [propertyName]: value,
              }
            : data;
        }),
      })
    );
  };

  const updateMetaDataFromName = (propertyName: string, value: string) => {
    dispatch(
      setWall({
        ...wall,
        parts: wall.parts.map((part) => {
          if (part.type === glassWallPartTypes.door) {
            return {
              ...part,
              doorGlassNames: {
                ...doorGlassNames,
                [propertyName]: value,
              },
            };
          }
          return part;
        }),
        doorGlassNames: {
          ...doorGlassNames,
          [propertyName]: value,
        },
      })
    );
  };

  const setEdgeTreatmentType = (e: React.ChangeEvent<HTMLInputElement>) =>
    updateDoorMetaData(`${selectedGlass}EdgeTreatmentType`, e.target.value);

  const setGlassPropertyType = (e: React.ChangeEvent<HTMLInputElement>) =>
    updateDoorMetaData(`${selectedGlass}GlassPropertyType`, e.target.value);

  const setGlassTreatmentType = (e: React.ChangeEvent<HTMLInputElement>) =>
    updateDoorMetaData(`${selectedGlass}GlassTreatmentType`, e.target.value);

  const setGlassName = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateMetaDataFromName(`${selectedGlass}Name`, e.target.value);
    updateDoorMetaData(`${selectedGlass}Name`, e.target.value);
  };

  const setGlassNotice = (e: React.ChangeEvent<HTMLInputElement>) =>
    updateDoorMetaData(`${selectedGlass}Notice`, e.target.value);

  return (
    <>
      <Row>
        <Col>
          <SelectInputTranslated
            label={t(
              `${translations.glassWallDoorProperties}:fields:Door glasses`
            )}
            onChange={(e) => setSelectedGlass(e.target.value)}
            optionKeyBase="door-glasses"
            options={doorGlassPlacementTypes}
            translationPath={`${translations.glassWallDoorProperties}:glassPlacementTypes`}
            value={selectedGlass}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <SelectInputTranslated
            disabled={disableInputs}
            label={t(`${translations.glassWallGlassProperties}:Edge treatment`)}
            onChange={setEdgeTreatmentType}
            options={glassEdgeTreatmentTypes}
            optionKeyBase="door-edge-treatment-type"
            required
            translationPath={`${translations.glassWallGlassProperties}:edgeTreatmentTypes`}
            value={edgeTreatmentType}
          />
        </Col>
        <Col>
          <SelectInputTranslated
            disabled={disableInputs}
            label={t(`${translations.glassWallGlassProperties}:Glass property`)}
            optionKeyBase="door-glass-property-type"
            onChange={setGlassPropertyType}
            options={glassPropertyTypes}
            required
            translationPath={`${translations.glassWallGlassProperties}:glassPropertyTypes`}
            value={glassPropertyType}
          />
        </Col>
        <Col>
          <SelectInputTranslated
            disabled={disableInputs}
            label={t(
              `${translations.glassWallGlassProperties}:Glass treatment`
            )}
            onChange={setGlassTreatmentType}
            options={glassTreatmentTypes}
            optionKeyBase="door-glass-treatment-type"
            required
            translationPath={`${translations.glassWallGlassProperties}:glassTreatmentTypes`}
            value={glassTreatmentType}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <TextInputField
            id="door-glass-name"
            label={t(
              `${translations.glassWallDoorProperties}:fields:Glass Name`
            )}
            onChange={setGlassName}
            name="doorGlassName"
            value={glassName}
            disabled={disableInputs}
          />
        </Col>
        <Col>
          <TextInputField
            id="door-glass-notice"
            label={t(`${translations.glassWallGlassProperties}:fields:Notice`)}
            onChange={setGlassNotice}
            name="doorGlassNotice"
            value={glassNotice}
            disabled={disableInputs}
          />
        </Col>
      </Row>
    </>
  );
};

export default DoorGlasses;
